/**
 * AWS WAF Challenge/CAPTCHA integration for the two public forms
 * (GetStarted -> POST /api/leads, ContactUs -> POST /api/contact).
 *
 * The `linok-{env}-api-waf` web ACL on the linok-api ALB carries a silent `Challenge` rule on those
 * paths plus a CAPTCHA rule for abusive sources (cdk `WafStack`, `config.waf.captcha`). A browser
 * proves itself by attaching an `x-aws-waf-token` header, minted client-side by the WAF integration
 * SDK. The header - not the `aws-waf-token` cookie - is what makes this work: every call here is
 * cross-origin (`linoktech.com` -> `api.linoktech.com`), and the ACL's `tokenDomains` is what makes a
 * token minted on one subdomain valid at the other.
 *
 * The SDK is not on npm. It is served from the deployed ACL's `ApplicationIntegrationURL`, which is an
 * attribute of the live ACL rather than of the CloudFormation template - hence it arrives as a build
 * arg read from SSM by `.github/workflows/deploy.yml`, never as a hand-entered GitHub variable. If the
 * ACL is ever recreated the URL changes, and only a value read at build time follows it.
 *
 * Two failure modes are deliberately silent:
 * - `VITE_WAF_INTEGRATION_URL` empty (local dev, and any build made before the WAF deploy landed) ->
 *   `getWafToken()` resolves to `null` and the forms POST without the header.
 * - The script 404s, is blocked by an ad blocker or a corporate proxy, or `getToken()` throws ->
 *   also `null`.
 *
 * In both cases the request MUST still be sent, untokened, and WAF decides. A frontend that refused to
 * submit would turn an SDK outage into a self-inflicted form outage that WAF itself would not have
 * caused.
 */

const INTEGRATION_URL: string = import.meta.env.VITE_WAF_INTEGRATION_URL || '';

/** The domain-scoped CAPTCHA API key. Not a secret: it ships in this public bundle by design. */
export const WAF_CAPTCHA_API_KEY: string = import.meta.env.VITE_WAF_CAPTCHA_API_KEY || '';

/** True when both build-time values are present, i.e. the puzzle can actually be rendered. */
export const wafCaptchaConfigured = Boolean(INTEGRATION_URL && WAF_CAPTCHA_API_KEY);

/**
 * Resolves the script URL from whatever shape the integration URL arrives in.
 *
 * AWS's documented CAPTCHA integration URL is a COMPLETE script URL
 * (`https://<hash>.<region>.captcha-sdk.awswaf.com/<hash>/jsapi.js`), not a base path - but the
 * console and the API have not been consistent about this, so the build arg could be either. Blindly
 * appending to a URL that already names the file requests `.../jsapi.js/jsapi.js`, which 404s
 * SILENTLY: a load failure resolves to "no token" by design, so the only symptom is
 * `SensitivePathChallenge` requests arriving without a valid token. That is the hardest failure in
 * this integration to trace back to a cause, and this two-line guard removes the whole class.
 *
 * Take the URL as-is when it already names a `.js` file; otherwise treat it as a base.
 */
export const wafSdkSrc = (url: string): string =>
    url.endsWith('.js') ? url : `${url.endsWith('/') ? url : `${url}/`}jsapi.js`;

let sdkPromise: Promise<void> | null = null;

/**
 * Injects the WAF integration SDK exactly once, memoised in a module-level promise.
 *
 * `jsapi.js` - the CAPTCHA JavaScript API - rather than `challenge.js`, on purpose: it is a superset,
 * exposing `AwsWafIntegration.getToken()` for the silent path *and* `AwsWafCaptcha.renderCaptcha()`
 * for the puzzle, so one script tag covers both. (`challenge.js`, served from the same URL, has
 * `AwsWafIntegration` only.) The SDK starts acquiring a token as soon as it loads, which is why the
 * form components call this on mount - it gets the whole time the user spends typing.
 *
 * See `wafSdkSrc` for why the URL is not just concatenated. Verify the resolved `script.src` in
 * DevTools the first time this is wired against a real ACL: a 404 here is silent by design.
 *
 * Never rejects. A load failure resolves, and `getWafToken()` then returns `null`.
 */
export const ensureWafSdk = (): Promise<void> => {
    if (sdkPromise) return sdkPromise;

    if (!INTEGRATION_URL || typeof document === 'undefined') {
        sdkPromise = Promise.resolve();
        return sdkPromise;
    }

    sdkPromise = new Promise<void>((resolve) => {
        const script = document.createElement('script');
        script.src = wafSdkSrc(INTEGRATION_URL);
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => resolve();
        document.head.appendChild(script);
    });

    return sdkPromise;
};

/**
 * Resolves to a WAF token, or `null` if one cannot be obtained for any reason.
 *
 * Callers must treat `null` as "send the request without the header", never as "block the submit".
 */
export const getWafToken = async (): Promise<string | null> => {
    try {
        await ensureWafSdk();
        if (typeof window === 'undefined' || !window.AwsWafIntegration) return null;
        const token = await window.AwsWafIntegration.getToken();
        return token || null;
    } catch (err) {
        console.warn('WAF token unavailable; submitting without it', err);
        return null;
    }
};

/** Spread into a request's headers. Empty when no token could be minted. */
export const wafTokenHeader = async (): Promise<Record<string, string>> => {
    const token = await getWafToken();
    return token ? { 'x-aws-waf-token': token } : {};
};

/**
 * WAF answers a challenged request it will not let through with `202` (challenge/captcha interstitial)
 * or `405`. Branch on the status code, not on the `x-amzn-waf-action` response header: that header is
 * emitted by WAF on a cross-origin response, so linok-api's `exposedHeaders` does not apply to it and
 * there is no guarantee JS can read it.
 */
export const isWafChallengeStatus = (status: number | undefined): boolean =>
    status === 202 || status === 405;
